import {z} from 'zod/v4';

export const globalUserJwtPayloadSchema = z.object({
  token_type: z.literal('Bearer'),
  access_token: z.string(),
  refresh_token: z.string(),
  expires_in: z.number(),
});

export type GlobalUserJWTPayload = z.infer<typeof globalUserJwtPayloadSchema>;

// @see apps/api-core.patientrewardshub.com/globaluser/Enums/GlobalUserTokenScope.php
type GlobalUserScope = 'update_profile';

export type DecodedGlobalUserJwt = {
  globalUserId: number; // sub
  issuedAt: number; // iat
  expiresAt: number; // exp
  scopes: GlobalUserScope[];
};

type DecodedGlobalUserJwtRaw = {
  /** global user id */
  sub: string;
  /** issued at */
  iat: number;
  /** expires at */
  exp: number;
  aud: number;
  scopes: GlobalUserScope[];
};

const prettifyDecodedGlobalUserJwt = (
  raw: DecodedGlobalUserJwtRaw
): DecodedGlobalUserJwt => {
  return {
    globalUserId: Number.parseInt(raw.sub, 10),
    issuedAt: raw.iat,
    expiresAt: raw.exp,
    scopes: raw.scopes,
  };
};

const decodeIncomingJwt = (encoded: string) => {
  if (typeof window !== 'undefined') {
    // Browser environment
    return atob(encoded);
  }
  // Node.js environment
  return Buffer.from(encoded, 'base64').toString();
};

export const decodeGlobalUserJwt = (jwt: string): DecodedGlobalUserJwt => {
  const segments = jwt.split('.');

  if (segments.length !== 3)
    throw new Error(
      "JWT is malformed. It must be of the form 'header.payload.signature'"
    );

  try {
    const payload = segments[1];
    return prettifyDecodedGlobalUserJwt(JSON.parse(decodeIncomingJwt(payload)));
  } catch (err) {
    throw new Error(
      `JWT payload could not be parsed - ${(err as Error).message}`
    );
  }
};
